import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, LucideIcon } from "lucide-react";
import { Button } from "@/ui-components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/ui-components/ui/card";
import { cn } from "@/lib/utils/cn";
import { PageHeader, PageLayout, PageTitle } from "./page-layout";
import { toast } from "@/lib/toast";

export type CardDashboardItem = {
  title: string;
  description: string;
  icon: LucideIcon;
  path: string;
  color?: string;
  bgColor?: string;
  borderColor?: string;
  disabled?: boolean;
  disabledMessage?: string;
};

type CardDashboardPageProps = {
  basePath: string;
  pageTitle: string;
  pageIcon: LucideIcon;
  pageIconColor?: string;
  pageIconBg?: string;
  sectionTitle?: string;
  sectionDescription?: string;
  items: CardDashboardItem[];
  className?: string;
};

export const CardDashboardPage: React.FC<CardDashboardPageProps> = ({
  basePath,
  pageTitle,
  pageIcon: PageIcon,
  pageIconColor,
  pageIconBg,
  sectionTitle,
  sectionDescription,
  items,
  className,
}) => {
  const navigate = useNavigate();

  return (
    <PageLayout basePath={basePath}>
      <PageHeader>
        <PageTitle title={pageTitle} icon={PageIcon} iconColor={pageIconColor} iconBg={pageIconBg} />
      </PageHeader>

      <div className={cn("p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto w-full space-y-8", className)}>
        <div>
          {sectionTitle ? <h2 className="text-xl font-semibold text-gray-900 mb-2">{sectionTitle}</h2> : null}
          {sectionDescription ? <p className="text-gray-500 mb-6">{sectionDescription}</p> : null}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((item) => {
              const isDisabled = Boolean(item.disabled);
              const bgColor = item.bgColor ?? "bg-blue-50";
              const borderColor = item.borderColor ?? "border-blue-100";
              const color = item.color ?? "text-blue-600";

              return (
                <Card
                  key={item.path}
                  className={cn(
                    "group relative overflow-hidden border-gray-200 hover:border-blue-200 hover:shadow-md transition-all",
                    isDisabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
                  )}
                  onClick={() => {
                    if (!isDisabled) {
                      navigate(item.path);
                    } else if (item.disabledMessage) {
                      toast.error(item.disabledMessage);
                    }
                  }}
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div
                        className={cn(
                          "h-12 w-12 rounded-2xl border flex items-center justify-center",
                          bgColor,
                          borderColor
                        )}
                      >
                        <item.icon className={cn("h-6 w-6", color)} />
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                          "text-gray-400 group-hover:text-blue-600 -mr-2 -mt-2",
                          isDisabled ? "pointer-events-none" : undefined
                        )}
                      >
                        <ArrowRight className="h-5 w-5" />
                      </Button>
                    </div>
                    <CardTitle className="mt-4 text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm text-gray-500 line-clamp-2">{item.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

